import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import IconButton from "../Buttons/IconButton";
import Loader from "../Loaders/Loader";
import { ConfirmAlert } from "../Alerts/Alerts";
import { useTranslationApp } from "../../lib/i18next";
import {
  BUTTON_CANCEL,
  BUTTON_SEND,
  CONTENT_APPROVAL_CONFIRM_ENABLE_DESCRIPTION,
  CONTENT_APPROVAL_CONFIRM_ENABLE_TITLE,
  CAMPAIGN_APPROVAL_SEND_FOR_REVIEW,
} from "../../i18n/keysTranslations";
import {
  getCampaignApprovalStatusLabel,
  getCampaignApprovalStatusSx,
} from "../../pages/CampaignApproval/utils";
import {
  BOX_SHADOW_BUTTON,
  INPUT_COLOR_BORDER,
  PRIMARY_COLOR_TEXT,
} from "../../utils/colors";
import { BORDER_RADIUS_BUTTON } from "../../utils/constants";

const ContentApprovalControls = ({
  approvalData,
  isEnabling = false,
  disabled = false,
  showEnableButton = true,
  onEnableApproval,
  onOpenReview,
  overrideRejectedLabel,
}) => {
  const { t } = useTranslationApp();
  const isEnabled = Boolean(approvalData?.enabled);
  const approvalStatus = approvalData?.status;

  const handleOpenReview = (event) => {
    event?.stopPropagation();
    if (disabled || isEnabling) {
      return;
    }
    onOpenReview?.();
  };

  const onRequestEnableApproval = async (event) => {
    event?.stopPropagation();
    if (disabled || isEnabling) {
      return;
    }

    const result = await ConfirmAlert({
      title: t(CONTENT_APPROVAL_CONFIRM_ENABLE_TITLE),
      text: t(CONTENT_APPROVAL_CONFIRM_ENABLE_DESCRIPTION),
      confirmButtonText: t(BUTTON_SEND),
      cancelButtonText: t(BUTTON_CANCEL),
    });

    if (result.isConfirmed) {
      await onEnableApproval?.();
    }
  };

  const statusSx = isEnabled
    ? getCampaignApprovalStatusSx(approvalStatus)
    : null;

  const statusLabel =
    approvalStatus === "rejected" && overrideRejectedLabel
      ? overrideRejectedLabel
      : getCampaignApprovalStatusLabel(approvalStatus, t);

  if (!isEnabled) {
    if (!showEnableButton) {
      return null;
    }

    return (
      <IconButton
        onClick={onRequestEnableApproval}
        type="icon"
        edge={false}
        disabled={disabled || isEnabling}
        loading={isEnabling}
        loaderColor={PRIMARY_COLOR_TEXT}
        tooltipText={CAMPAIGN_APPROVAL_SEND_FOR_REVIEW}
      >
        <FactCheckOutlinedIcon
          sx={{ color: PRIMARY_COLOR_TEXT, fill: PRIMARY_COLOR_TEXT }}
        />
      </IconButton>
    );
  }

  return (
    <Box
      onClick={handleOpenReview}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        border: `1px solid ${INPUT_COLOR_BORDER}`,
        borderRadius: BORDER_RADIUS_BUTTON,
        boxShadow: BOX_SHADOW_BUTTON,
        height: 32,
        px: 1,
        bgcolor: "background.paper",
        cursor: disabled || isEnabling ? "not-allowed" : "pointer",
        opacity: disabled || isEnabling ? 0.6 : 1,
        userSelect: "none",
        flexShrink: 0,
      }}
    >
      {isEnabling ? (
        <Loader hasMarginTop={false} size={16} />
      ) : (
        <Typography
          variant="caption"
          sx={{
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.4px",
            fontSize: "0.62rem",
            color: statusSx?.color || "text.primary",
            whiteSpace: "nowrap",
          }}
        >
          {statusLabel}
        </Typography>
      )}
    </Box>
  );
};

export default ContentApprovalControls;
